import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { cn, renderDisplayText } from "@/lib/utils";
import { LoadingLabel, Skel } from "@/components/home/TenantSkeletons";
import { GoogleGlyph } from "@/components/reviews/GoogleGlyph";
import type { ReviewsModel, ServicesModel, TrustItem } from "@/components/home/layouts/models";
import {
  Avatar, HeroActions, HeroCopySkeleton, HeroTitle, Photo, ReviewExtras, ReviewerLine, StarRow,
  type HeroViewProps,
} from "@/components/home/layouts/shared";

/** Modern: dark ink band, photo in a floating card, teal highlight. */
export function ModernHero({ m, edit }: HeroViewProps) {
  const seq = (n: number) => ({ ["--seq" as string]: n });
  return (
    <section className="v-modern-hero relative overflow-hidden bg-[hsl(245_35%_12%)] pb-28 pt-12 text-white md:pb-40 md:pt-20" aria-busy={!m.ready}>
      <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(50% 70% at 85% 10%, hsl(var(--accent) / 0.28), transparent 70%), radial-gradient(40% 50% at 0% 100%, hsl(var(--primary) / 0.55), transparent 70%)" }} aria-hidden />
      <div className="container relative mx-auto px-5 md:px-6">
        <div className="grid items-center gap-14 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-7">
            {m.ready ? (
              <>
                <p className="tp-seq inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-4 py-1.5 text-sm font-medium text-white/85" style={seq(0)}>
                  <span className="h-2 w-2 rounded-full bg-accent" aria-hidden />{m.eyebrow}
                </p>
                <HeroTitle m={m} edit={edit} className="tp-display tp-display--md v-modern-title tp-seq mt-7 !text-white" />
                <div className="tp-lead tp-seq mt-7 max-w-xl !text-white/70" style={seq(2)}>{edit?.subtitle ?? <p>{m.subtitle}</p>}</div>
                <div className="tp-seq mt-10" style={seq(3)}><HeroActions m={m} edit={edit} ghostClassName="!border-white/25 !bg-white/5 !text-white hover:!bg-white/10" /></div>
              </>
            ) : <HeroCopySkeleton className="opacity-60" />}
          </div>
          <div className="relative lg:col-span-5">
            <div className="tp-hero-media relative mx-auto max-w-md rounded-[1.75rem] bg-white/[0.08] p-3 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.7)] ring-1 ring-white/15 backdrop-blur lg:ml-auto lg:rotate-[1.5deg]">
              <div className="relative aspect-[4/4.6] overflow-hidden rounded-[1.25rem] bg-muted">
                {m.heroPending ? <Skel className="absolute inset-0 h-full w-full rounded-none" /> : edit?.image ?? <Photo src={m.heroImage} alt="Professional cleaner at work" fetchPriority="high" decoding="async" />}
              </div>
              <div className="tp-hero-float absolute -bottom-6 -left-5 flex items-center gap-3 rounded-2xl bg-accent px-5 py-3 text-[hsl(245_35%_10%)] shadow-xl sm:-left-10">
                {m.reviewsPending ? <Skel className="h-6 w-24 tp-skel-round" /> : (<><span className="tp-numeral text-3xl">{m.rating.toFixed(1)}</span><span className="text-xs font-semibold leading-tight">{m.ratingLabel}<br /><span className="font-normal">{m.reviewCount > 0 ? `${m.reviewCount} reviews` : "Loved locally"}</span></span></>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function ModernTrust({ items }: { items: TrustItem[] }) {
  return (
    <section aria-label="Why customers trust us" className="relative z-10 -mt-14 pb-6 md:-mt-20">
      <div className="container mx-auto px-5 md:px-6">
        <ul className="grid gap-4 md:grid-cols-3">
          {items.slice(0, 3).map(({ key, Icon, title, subtitle }, i) => (
            <li key={key} className="flex items-center gap-4 rounded-2xl border border-border bg-card p-5 shadow-[0_20px_40px_-24px_hsl(var(--primary)/0.35)]" data-reveal style={{ ["--reveal-delay" as string]: `${i * 80}ms` }}>
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-accent/15 text-accent"><Icon className="h-5 w-5" strokeWidth={1.75} /></span>
              <span className="leading-tight"><span className="block font-semibold text-foreground">{title}</span><span className="mt-1 block text-sm text-muted-foreground">{subtitle}</span></span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function spans(count: number, i: number): string {
  const L = "md:col-span-2 md:row-span-2";
  const table: Record<number, string[]> = {
    1: ["md:col-span-4 md:row-span-2"],
    2: [L, L],
    3: [L, "md:col-span-2", "md:col-span-2"],
    4: [L, "md:col-span-2", "md:col-span-1", "md:col-span-1"],
    5: [L, "md:col-span-1", "md:col-span-1", "md:col-span-2", "md:col-span-4"],
  };
  if (table[count]) return table[count][i];
  if (i === 0) return L;
  if (i < 5) return "md:col-span-1";
  return i === count - 1 && (count - 5) % 2 === 1 ? "md:col-span-4" : "md:col-span-2";
}

export function ModernServices({ s }: { s: ServicesModel }) {
  return (
    <section className="tp-section">
      <div className="container mx-auto px-5 md:px-6">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-xl">
            <p className="tp-eyebrow" data-reveal>What we do</p>
            <h2 className="tp-h2 mt-5" data-reveal>{renderDisplayText(s.title)}</h2>
          </div>
          <Link to={s.servicesHref} className="tp-link text-sm" data-reveal>View all services <ArrowRight className="h-4 w-4" /></Link>
        </div>
        {s.loading ? (
          <div className="tp-skeleton mt-12 grid gap-4 md:grid-cols-4" aria-busy="true"><LoadingLabel>Loading services</LoadingLabel><Skel className="h-80 rounded-3xl md:col-span-2 md:row-span-2" /><Skel className="h-36 rounded-3xl md:col-span-2" /><Skel className="h-36 rounded-3xl md:col-span-2" /></div>
        ) : (
          <div className="mt-12 grid auto-rows-[15rem] gap-4 md:mt-16 md:grid-cols-4 md:gap-5">
            {s.items.map((item, i) => {
              const hero = i === 0;
              const ink = !hero && i % 3 === 1;
              return (
                <Link key={item.key} to={s.servicesHref} className={cn("group relative flex min-h-[15rem] flex-col justify-end overflow-hidden rounded-3xl border border-border", spans(s.items.length, i), ink ? "bg-[hsl(245_35%_14%)] text-white" : "bg-muted")} data-reveal style={{ ["--reveal-delay" as string]: `${i * 70}ms` }}>
                  {!ink ? (
                    <>
                      <Photo src={item.image} alt={item.category} className="tp-zoom" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[hsl(245_35%_8%/0.88)] via-[hsl(245_35%_8%/0.2)] to-transparent" aria-hidden />
                    </>
                  ) : (
                    <div className="absolute right-4 top-4 h-20 w-20 overflow-hidden rounded-2xl ring-1 ring-white/20"><Photo src={item.image} alt="" loading="lazy" /></div>
                  )}
                  <div className="relative flex items-end justify-between gap-4 p-6 text-white">
                    <div className="min-w-0">
                      <span className="mb-3 inline-block rounded-full bg-accent px-3 py-1 text-xs font-semibold text-[hsl(245_35%_10%)]">{item.price}</span>
                      <h3 className={cn("tp-h3 !text-white", hero && "!text-[clamp(1.5rem,1.1rem+1.5vw,2.4rem)]")}>{renderDisplayText(item.title)}</h3>
                      {item.description && <p className={cn("mt-2 text-sm leading-relaxed text-white/75", !hero && "line-clamp-2")}>{item.description}</p>}
                    </div>
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/15 transition-colors group-hover:bg-accent group-hover:text-[hsl(245_35%_10%)]" aria-hidden><ArrowUpRight className="h-4 w-4" /></span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

export function ModernReviews({ r, reviewFormHref }: { r: ReviewsModel; reviewFormHref?: string }) {
  return (
    <section className="tp-section tp-tint" aria-busy={r.pending}>
      <div className="container mx-auto px-5 md:px-6">
        <div className="grid gap-5 lg:grid-cols-12">
          <div className="flex flex-col justify-between rounded-3xl bg-[hsl(245_35%_12%)] p-8 text-white lg:col-span-4 lg:p-10">
            <div>
              <p className="tp-eyebrow !text-white/80">Reviews</p>
              <h2 className="tp-h2 mt-5 !text-white">What our customers say</h2>
            </div>
            <div className="mt-10">
              {r.pending ? <Skel className="h-20 w-40" /> : (
                <>
                  <p className="tp-numeral text-[5rem] text-accent">{r.average.toFixed(1)}</p>
                  <StarRow n={r.average} className="mt-2 text-lg !text-accent" />
                  <p className="mt-3 text-sm text-white/70">{r.label} · {r.countLine}</p>
                </>
              )}
              {!r.pending && <ReviewExtras google={r.google} reviewFormHref={reviewFormHref} className="mt-6 [&_a:not(.tp-btn)]:!text-white" />}
            </div>
          </div>
          {r.pending ? (
            <div className="tp-skeleton grid gap-5 sm:grid-cols-2 lg:col-span-8" aria-hidden><LoadingLabel>Loading reviews</LoadingLabel><Skel className="h-56 rounded-3xl" /><Skel className="h-56 rounded-3xl" /></div>
          ) : (
            <div className="grid gap-5 sm:grid-cols-2 lg:col-span-8">
              {r.items.map((item, i) => (
                <figure key={item.key} className={cn("flex flex-col rounded-3xl border border-border bg-card p-7", i === 0 && r.items.length % 2 === 1 && "sm:col-span-2")} data-reveal data-testid={item.google ? "google-review-card" : undefined}>
                  <div className="flex items-center justify-between"><StarRow n={item.rating} className="text-sm !text-accent" />{item.google && <GoogleGlyph className="h-5 w-5" />}</div>
                  <blockquote className="mt-4 flex-1"><p className="line-clamp-6 text-[1.05rem] leading-relaxed text-foreground">{item.quote}</p></blockquote>
                  <figcaption className="mt-6 flex items-center gap-3 border-t border-border pt-5"><Avatar item={item} className="h-10 w-10" /><div className="min-w-0"><ReviewerLine item={item} /></div></figcaption>
                </figure>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
